import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScrollToTopButton from '../components/ScrollToTopButton';
import Banner from '../components/Banner';
import ContactForm from '../components/ContactForm';
import { editorialesData, publiData, videoclipsData, colaboracionesData } from '../data/projects';
import { placeholderImages } from '../utils/placeholders';
import type { CarouselItem } from '../types';

const HomePage: React.FC = () => {
  // Restaurar filtro y posición al volver atrás (evento popstate)
  useEffect(() => {
    const onPopState = () => {
      const lastFilter = sessionStorage.getItem('lastFilter');
      const lastProjectId = sessionStorage.getItem('lastProjectId');
      const lastShouldShowAll = sessionStorage.getItem('lastShouldShowAll') === 'true';
      if (lastFilter) {
        setActiveFilter(lastFilter);
        setShowAllProjects(lastShouldShowAll);
        if (lastProjectId) {
          setTimeout(() => {
            const projectElement = document.querySelector(`[data-project-id="${lastProjectId}"]`);
            if (projectElement) {
              const header = document.getElementById('header');
              const headerHeight = header ? header.offsetHeight + 20 : 100;
              const elementPosition = projectElement.getBoundingClientRect().top + window.pageYOffset;
              window.scrollTo({ top: elementPosition - headerHeight, behavior: 'smooth' });
            }
          }, 100);
        }
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState('todos');
  const [showAllProjects, setShowAllProjects] = useState(false);

  // Función para obtener todos los proyectos de una categoría específica
  const getAllProjectsByCategory = (category: string): CarouselItem[] => {
    switch (category) {
      case 'editoriales':
        return editorialesData;
      case 'colaboraciones':
        return colaboracionesData;
      case 'videoclips':
        return videoclipsData;
      case 'publi':
        return publiData;
      default:
        return [...editorialesData, ...colaboracionesData, ...videoclipsData, ...publiData];
    }
  };

  // Función para obtener proyectos destacados (para filtro "todos": 2 de cada, otros: 4)
  const getFeaturedProjectsByRows = (): CarouselItem[] => {
    const projects: CarouselItem[] = [];
    
    // Para "todos": 2 de cada categoría, para otros: 4
    const projectsToShow = activeFilter === 'todos' ? 2 : 4;
    
    if (activeFilter === 'todos') {
      // Mostrar 2 de cada categoría
      projects.push(...editorialesData.slice(0, projectsToShow));
      projects.push(...colaboracionesData.slice(0, projectsToShow));
      projects.push(...videoclipsData.slice(0, projectsToShow));
      projects.push(...publiData.slice(0, projectsToShow));
    }
    
    return projects;
  };

  // Función para obtener todos los proyectos organizados por secciones (excluyendo los ya mostrados)
  const getAllProjectsBySections = (): CarouselItem[] => {
    const projects: CarouselItem[] = [];
    const sectionsToShow = ['editoriales', 'colaboraciones', 'videoclips', 'publi'];
    
    sectionsToShow.forEach(section => {
      const sectionData = getAllProjectsByCategory(section);
      // Excluir los primeros 2 que ya se mostraron en destacados
      const remainingProjects = sectionData.slice(2);
      projects.push(...remainingProjects);
    });
    
    return projects;
  };

  // Determinar qué proyectos mostrar según el estado
  const getProjectsToShow = () => {
    if (showAllProjects) {
      if (activeFilter === 'todos') {
        // Mostrar destacados (2 de cada) + el resto organizados por secciones
        const featured = getFeaturedProjectsByRows();
        const remaining = getAllProjectsBySections();
        return [...featured, ...remaining];
      } else {
        // Mostrar todos los proyectos de la categoría seleccionada
        return getAllProjectsByCategory(activeFilter);
      }
    } else {
      // Mostrar solo proyectos destacados
      if (activeFilter === 'todos') {
        return getFeaturedProjectsByRows();
      } else {
        // Para categorías específicas, mostrar 4 proyectos destacados
        return getAllProjectsByCategory(activeFilter).slice(0, 4);
      }
    }
  };

  const filteredProjects = getProjectsToShow();

  // Función para obtener el total de proyectos según el filtro activo
  const getTotalProjectsCount = () => {
    if (activeFilter === 'todos') {
      return editorialesData.length + colaboracionesData.length + videoclipsData.length + publiData.length;
    } else {
      return getAllProjectsByCategory(activeFilter).length;
    }
  };

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setShowAllProjects(false);
    // Guardar el filtro y vista en sessionStorage
    sessionStorage.setItem('lastFilter', filter);
    sessionStorage.setItem('lastShouldShowAll', 'false');
    sessionStorage.setItem('lastProjectId', '');
  };

  const handleShowAllProjects = () => {
    setShowAllProjects(true);
    // Guardar la vista en sessionStorage
    sessionStorage.setItem('lastShouldShowAll', 'true');
  };

  const handleShowFeaturedProjects = () => {
    setShowAllProjects(false);
    // Guardar la vista en sessionStorage
    sessionStorage.setItem('lastShouldShowAll', 'false');
    // Smooth scroll al inicio de la sección de proyectos
    setTimeout(() => {
      const projectsSection = document.getElementById('proyectos-destacados');
      if (projectsSection) {
        const header = document.getElementById('header');
        const headerHeight = header ? header.offsetHeight + 20 : 100;
        const elementPosition = projectsSection.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - headerHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Escuchar eventos de cambio de filtro desde el menú de navegación
  useEffect(() => {
    const handleFilterEvent = (event: CustomEvent) => {
      const { filter } = event.detail;
      // Convertir "todos" a "todos" para consistencia
      setActiveFilter(filter === 'todos' ? 'todos' : filter);
      // Restablecer a mostrar solo proyectos destacados al cambiar filtro desde navegación
      setShowAllProjects(false);
    };

    window.addEventListener('changeProjectFilter', handleFilterEvent as EventListener);
    
    return () => {
      window.removeEventListener('changeProjectFilter', handleFilterEvent as EventListener);
    };
  }, []);

  // Manejar navegación desde páginas de proyecto (como Cold Memories)
  // Guardar el último filtro y proyecto en sessionStorage para restaurar al volver atrás
  useEffect(() => {
    if (location.state) {
      const { filter, projectId, shouldShowAll, resetToTop } = location.state as {
        filter?: string;
        projectId?: string;
        shouldShowAll?: boolean;
        resetToTop?: boolean;
      };

      // Guardar el estado en sessionStorage
      sessionStorage.setItem('lastFilter', filter || 'todos');
      sessionStorage.setItem('lastProjectId', projectId || '');
      sessionStorage.setItem('lastShouldShowAll', shouldShowAll ? 'true' : 'false');

      if (resetToTop) {
        setActiveFilter('todos');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      if (filter) {
        setActiveFilter(filter);
        if (projectId) {
          const scrollToProject = (showAll: boolean = false) => {
            setTimeout(() => {
              const projectElement = document.querySelector(`[data-project-id="${projectId}"]`);
              if (projectElement) {
                const header = document.getElementById('header');
                const headerHeight = header ? header.offsetHeight + 20 : 100;
                const elementPosition = projectElement.getBoundingClientRect().top + window.pageYOffset;
                window.scrollTo({ top: elementPosition - headerHeight, behavior: 'smooth' });
              } else if (!showAll) {
                setShowAllProjects(true);
                setTimeout(() => scrollToProject(true), 100);
              }
            }, 100);
          };
          if (shouldShowAll) {
            setShowAllProjects(true);
            scrollToProject(true);
          } else {
            const categoryData = getAllProjectsByCategory(filter);
            const featuredProjects = categoryData.slice(0, 4);
            const projectInFeatured = featuredProjects.find(p => p.id === projectId);
            if (projectInFeatured) {
              setShowAllProjects(false);
              scrollToProject(false);
            } else {
              setShowAllProjects(true);
              scrollToProject(true);
            }
          }
        } else {
          setTimeout(() => {
            const projectsSection = document.getElementById('proyectos-destacados');
            if (projectsSection) {
              const header = document.getElementById('header');
              const headerHeight = header ? header.offsetHeight + 20 : 100;
              const elementPosition = projectsSection.getBoundingClientRect().top + window.pageYOffset;
              window.scrollTo({ top: elementPosition - headerHeight, behavior: 'smooth' });
            }
          }, 300);
        }
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } else {
      // Si no hay location.state, restaurar el filtro y posición desde sessionStorage
      const lastFilter = sessionStorage.getItem('lastFilter');
      const lastProjectId = sessionStorage.getItem('lastProjectId');
      const lastShouldShowAll = sessionStorage.getItem('lastShouldShowAll') === 'true';
      if (lastFilter) {
        setActiveFilter(lastFilter);
        setShowAllProjects(lastShouldShowAll);
        if (lastProjectId) {
          setTimeout(() => {
            const projectElement = document.querySelector(`[data-project-id="${lastProjectId}"]`);
            if (projectElement) {
              const header = document.getElementById('header');
              const headerHeight = header ? header.offsetHeight + 20 : 100;
              const elementPosition = projectElement.getBoundingClientRect().top + window.pageYOffset;
              window.scrollTo({ top: elementPosition - headerHeight, behavior: 'smooth' });
            }
          }, 100);
        }
      }
    }
  }, [location.state]);

  const getCategoryLabel = (category: string) => {
    const labels = {
      'editoriales': 'Editorial',
      'colaboraciones': 'Colaboraciones',
      'videoclips': 'Videoclips',
      'publi': 'Publi'
    };
    return labels[category as keyof typeof labels] || category;
  };

  const handleProjectClick = (project: CarouselItem) => {
    // Proyectos editoriales con páginas dedicadas
    const editorialPagesMap: { [key: string]: string } = {
      'cold-memories': '/proyectos/editoriales/cold-memories',
      'mental-breakdown': '/proyectos/editoriales/mental-breakdown',
      'xtrema-studio': '/proyectos/editoriales/xtrema-studio',
      'rouge': '/proyectos/editoriales/rouge',
      'demeo': '/proyectos/editoriales/demeo',
      'bodegon': '/proyectos/editoriales/bodegon',
      'boudoir': '/proyectos/editoriales/boudoir',
      'dysmorphia': '/proyectos/editoriales/dysmorphia',
      'gentleman': '/proyectos/editoriales/gentleman',
      'gravity': '/proyectos/editoriales/gravity',
      'jaque-al-rey': '/proyectos/editoriales/jaque-al-rey',
      'lagrima-santa': '/proyectos/editoriales/lagrima-santa',
      'street-edition': '/proyectos/editoriales/street-edition',
      'susurros-de-algodon': '/proyectos/editoriales/susurros-de-algodon',
      'whispers-of-the-twenties': '/proyectos/editoriales/whispers-of-the-twenties'
    };

    if (editorialPagesMap[project.id]) {
      navigate(editorialPagesMap[project.id]);
    } else {
      // Para otros proyectos, mostrar en consola (futuras páginas)
      console.log('Navegación a proyecto:', project.title);
      // Aquí puedes agregar más casos específicos cuando crees más páginas
    }
  };

  return (
    <div id="wrapper">
      <Header />
      
      {/* Hero Section - Visual Impactante */}
      <Banner
        title="Gema Ruiz"
        subtitle="Estilista profesional especializada en moda editorial, videoclips y producciones audiovisuales. Creando conceptos visuales únicos que transforman ideas en realidad."
        // NOTA: Referencias a videoclips mantenidas para estructura futura
        imageUrl={placeholderImages.banner}
        className="style1 hero-banner"
      />

      {/* Main Content */}
      <div id="main">
        
        {/* Proyectos Destacados - Directo en Home */}
        <section id="proyectos-destacados" className="featured-projects">
          <div className="inner">
            <header className="major special">
              <h2>{showAllProjects ? 'Todos los Proyectos' : 'Proyectos Destacados'}</h2>
              <p>
                {showAllProjects 
                  ? 'Explora todo mi portafolio de trabajos en estilismo y diseño de moda'
                  : 'Una selección de mis mejores trabajos en estilismo y diseño de moda'
                }
              </p>
            </header>
            
            {/* Filtro Dinámico */}
            <div className="project-filters">
              <button 
                className={`filter-btn ${activeFilter === 'todos' ? 'active' : ''}`}
                onClick={() => handleFilterChange('todos')}
              >
                Todos
              </button>
              <button 
                className={`filter-btn ${activeFilter === 'editoriales' ? 'active' : ''}`}
                onClick={() => handleFilterChange('editoriales')}
              >
                Editorial
              </button>
              <button 
                className={`filter-btn ${activeFilter === 'colaboraciones' ? 'active' : ''}`}
                onClick={() => handleFilterChange('colaboraciones')}
              >
                Colaboraciones
              </button>
              <button 
                className={`filter-btn ${activeFilter === 'videoclips' ? 'active' : ''}`}
                onClick={() => handleFilterChange('videoclips')}
              >
                Videoclips
              </button>
              <button 
                className={`filter-btn ${activeFilter === 'publi' ? 'active' : ''}`}
                onClick={() => handleFilterChange('publi')}
              >
                Publi
              </button>
            </div>

            {/* Projects Count */}
            <div className="projects-count">
              <p>
                {showAllProjects ? 
                  `${getTotalProjectsCount()} proyecto${getTotalProjectsCount() !== 1 ? 's' : ''}` :
                  `${filteredProjects.length} de ${getTotalProjectsCount()} proyecto${getTotalProjectsCount() !== 1 ? 's' : ''}`
                }
                {activeFilter !== 'todos' && ` en ${getCategoryLabel(activeFilter)}`}
              </p>
            </div>

            {/* Mosaico Dinámico de Proyectos */}
            <div className="projects-grid">
              {filteredProjects.map((project, index) => (
                <div 
                  key={`${project.category}-${index}`}
                  className="project-card"
                  data-project-id={project.id}
                  onClick={() => handleProjectClick(project)}
                >
                  <div className="project-image">
                    <img
                      src={project.imageUrl || 'https://pub-122ae695cb844f9984136ea14dacedd9.r2.dev/gema/placeholder.webp'}
                      alt={project.title}
                      srcSet={project.imageUrl
                        ? `
                            ${project.imageUrl.replace('.webp', '-400.webp')} 400w,
                            ${project.imageUrl.replace('.webp', '-800.webp')} 800w,
                            ${project.imageUrl} 1200w
                          `
                        : 'https://pub-122ae695cb844f9984136ea14dacedd9.r2.dev/gema/placeholder-400.webp 400w, https://pub-122ae695cb844f9984136ea14dacedd9.r2.dev/gema/placeholder-800.webp 800w, https://pub-122ae695cb844f9984136ea14dacedd9.r2.dev/gema/placeholder.webp 1200w'}
                      sizes="(max-width: 600px) 400px, (max-width: 1200px) 800px, 1200px"
                    />
                    <div className="project-overlay">
                      <div className="project-info">
                        <span className="project-category">{getCategoryLabel(project.category || '')}</span>
                        <h3 className="project-title">{project.title}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA para ver más proyectos */}
            {(!showAllProjects && filteredProjects.length < getTotalProjectsCount()) || showAllProjects ? (
              <div className="projects-cta">
                {!showAllProjects && filteredProjects.length < getTotalProjectsCount() ? (
                  <button 
                    onClick={handleShowAllProjects}
                    className="button"
                  >
                    <i className="fas fa-expand-alt"></i>
                    Ver Todos ({getTotalProjectsCount()})
                  </button>
                ) : showAllProjects ? (
                  <button 
                    onClick={handleShowFeaturedProjects}
                    className="button secondary"
                  >
                    <i className="fas fa-compress-alt"></i>
                    Mostrar Menos
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>

        {/* Contact Section - Completa con Hero y Formulario */}
        <section id="contacto" className="contact-section">
          <div className="inner">
            {/* Contact Hero */}
            <div className="contact-hero">
              <div className="contact-hero-content">
                <h2>
                  <span className="contact-title-accent">¿Listo para crear algo increíble?</span>
                  <span className="contact-title-main">¡Trabajemos juntos!</span>
                </h2>
                
                <div className="contact-hero-text">
                  <p className="hero-question">
                    <i className="fas fa-lightbulb"></i>
                    ¿Tienes una idea en mente?
                  </p>
                  <p className="hero-description">
                    Me especializo en <strong>estilismo editorial</strong>, <em>videoclips musicales</em>, 
                    {/* NOTA: Manteniendo referencias a videoclips para estructura futura */} 
                    <strong>colaboraciones con artistas</strong> y <em>campañas publicitarias</em>. 
                    Cada proyecto es una oportunidad para crear algo único y memorable.
                  </p>
                  
                  <div className="contact-badges">
                    <span className="badge">Editorial</span>
                    <span className="badge">Videoclips</span>
                    {/* NOTA: Categoría de videoclips mantenida para estructura futura */}
                    <span className="badge">Colaboraciones</span>
                    <span className="badge">Publicidad</span>
                  </div>
                </div>
              </div>
            </div>

            {/* CV Download Section */}
            <div className="cv-download-section">
              <div className="cv-download-card">
                <h3>Descarga mi CV</h3>
                <p>
                  Conoce más sobre mi experiencia, formación y trayectoria profesional 
                  en el mundo del estilismo y la moda.
                </p>
                <a 
                  href="/assets/CV GEMA RUIZ GORDILLO.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="cv-download-btn"
                  title="Descargar CV en PDF"
                >
                  <i className="fas fa-download"></i>
                  Descargar CV
                </a>
              </div>
            </div>

            {/* Contact Info Grid */}
            <div className="contact-info">
              <div className="contact-details">
                <h3>Información de Contacto</h3>
                <ul className="contact-list">
                  <li>
                    <a 
                      href="mailto:gema.ruiz.gordillo@gmail.com" 
                      className="contact-item-link"
                      title="Enviar email"
                    >
                      <i className="icon solid fa-envelope"></i>
                      <div>
                        <h4>Email</h4>
                        <span>gema.ruiz.gordillo@gmail.com</span>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="tel:+34652847120" 
                      className="contact-item-link"
                      title="Llamar por teléfono"
                    >
                      <i className="icon solid fa-phone"></i>
                      <div>
                        <h4>Teléfono</h4>
                        <span>+34 652 847 120</span>
                      </div>
                    </a>
                  </li>
                  <li className="static-item">
                    <i className="icon solid fa-map-marker-alt"></i>
                    <div>
                      <h4>Ubicación</h4>
                      <span>Madrid, España</span>
                    </div>
                  </li>
                </ul>

                <h4>Sígueme en Redes Sociales</h4>
                <ul className="social-links">
                  <li>
                    <a 
                      href="https://www.instagram.com/geemaa_rg/" 
                      className="social-link instagram" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      title="Sígueme en Instagram"
                    >
                      <i className="fab fa-instagram"></i>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://www.tiktok.com/@usuario" 
                      className="social-link tiktok" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      title="Sígueme en TikTok"
                    >
                      <i className="fab fa-tiktok"></i>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://www.linkedin.com/in/usuario" 
                      className="social-link linkedin" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      title="Conecta en LinkedIn"
                    >
                      <i className="fab fa-linkedin"></i>
                    </a>
                  </li>
                </ul>

                <div className="contact-note">
                  <strong>Tiempo de respuesta:</strong> Normalmente respondo en menos de 24 horas.
                </div>

                <div className="contact-cta">
                  <a 
                    href="tel:+34652847120" 
                    className="button secondary"
                    title="Llamar directamente"
                  >
                    <i className="fas fa-phone"></i>
                    Llamar Ahora
                  </a>
                </div>
              </div>
              
              <ContactForm />
            </div>
          </div>
        </section>

      </div>

      <ScrollToTopButton />
      <Footer />
    </div>
  );
};

export default HomePage;
